// The words the speller is taught on top of its dictionaries (research:
// docs/research/editor-error-underline.md §9.6): the project's names and
// places, from the lists the editor has loaded; the project's shared
// dictionary («Добавить в словарь», each word declined as its `kind` says);
// and the writer's own «Пропустить всё», kept in a pref. Built on the
// main thread, sent to the worker whole; equal lists make an equal key, so a
// refetch that changed nothing re-checks nothing.

import type { ProjectLexicon } from '@/editor/engine/projectLexicon';
import type { ISpellingWord } from '@/types';

/** A word of the project's dictionary, as the speller needs it. */
export type DictionaryWord = Pick<ISpellingWord, 'word' | 'kind'>;

export interface SpellWords {
    /** Character names as written on their cards, and the dictionary's
     *  `name` words — declined like a name. */
    names: readonly string[];
    /** Location and sub-location names (uppercase-folded), and the
     *  dictionary's `place` words — declined like a place. */
    places: readonly string[];
    /** The dictionary's words with no kind («Добавить в словарь»): known as written. */
    added: readonly string[];
    /** «Пропустить всё»: never flagged, never offered as a fix. */
    ignored: readonly string[];
}

export const NO_SPELL_WORDS: SpellWords = { names: [], places: [], added: [], ignored: [] };

/** Trimmed, without blanks or repeats, sorted — so equal lists are equal. */
const cleanList = (words: Iterable<string>) => [...new Set([...words].map(w => w.trim()).filter(w => w !== ''))].sort();

export function createSpellWords(
    lexicon: ProjectLexicon | null,
    dictionary: readonly DictionaryWord[],
    ignored: readonly string[],
): SpellWords {
    const ofKind = (kind: DictionaryWord['kind']) => dictionary.filter(w => w.kind === kind).map(w => w.word);
    const places = [...(lexicon?.locations ?? [])].flatMap(([name, subs]) => [name, ...subs]);
    return {
        names: cleanList([...(lexicon?.characters?.names ?? []), ...ofKind('name')]),
        places: cleanList([...places, ...ofKind('place')]),
        added: cleanList(ofKind(null)),
        ignored: cleanList(ignored),
    };
}

/** Equal for equal lists — the part of the dictionary version the words make. */
export function spellWordsKey(words: SpellWords): string {
    return [words.names, words.places, words.added, words.ignored].map(list => list.join('\n')).join('\n\n');
}

/** Whether `next` still holds every word `prev` taught — a word taken away
 *  cannot be untaught, so the engine is rebuilt. Ignored words are never
 *  taught, so they do not count. */
export function keepsEveryWord(prev: SpellWords, next: SpellWords): boolean {
    const allIn = (taught: readonly string[], now: readonly string[]) => {
        const set = new Set(now);
        return taught.every(w => set.has(w));
    };
    return allIn(prev.names, next.names) && allIn(prev.places, next.places) && allIn(prev.added, next.added);
}

/** A stored word list, read back: its strings, anything else dropped. */
export function normalizeWordList(raw: unknown): readonly string[] {
    return Array.isArray(raw) ? raw.filter((w): w is string => typeof w === 'string') : [];
}
